import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Music2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AccountMenu } from "@/components/AccountMenu";

const BG =
  "https://images.unsplash.com/photo-1562362898-d1a9d124fd77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const ProjectCard = ({ p, i, onOpen, onDelete }) => (
  <div
    data-testid="project-card-item"
    onClick={onOpen}
    className="rise group cursor-pointer stone-panel p-5 flex items-center justify-between hover:border-[#8B1E2B] transition-colors"
    style={{ animationDelay: `${0.05 * i}s` }}
  >
    <div className="flex items-center gap-4 min-w-0">
      <span className="font-gothic text-2xl text-[#5a1e28] group-hover:text-[#c92a3e] transition-colors">
        {String(i + 1).padStart(2, "0")}
      </span>
      <div className="min-w-0">
        <h3 className="font-gothic text-lg tracking-wider text-amber-100/90 truncate">{p.name}</h3>
        <p className="eyebrow mt-1">
          {p.key} {p.scale} · {p.bpm} BPM · {p.tracks.length} voices · {new Date(p.updated_at).toLocaleDateString()}
        </p>
      </div>
    </div>
    <button
      data-testid={`btn-delete-project-${p.id}`}
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      className="p-2 text-stone-500 hover:text-[#E11D48] transition-colors"
      aria-label="Delete project"
    >
      <Trash2 size={16} />
    </button>
  </div>
);

export default function Library() {
  const nav = useNavigate();
  const { isPro, openUpgrade, setUser } = useAuth();

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("billing")) return;
    api.stripeSync().then(setUser).catch(() => {});
    window.history.replaceState(null, "", "/library");
  }, [setUser]);
  const [projects, setProjects] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .listProjects()
      .then(setProjects)
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const create = async (demo) => {
    try {
      const p = await api.createProject({ name: demo ? "Dark Hymn Demo" : name.trim() || "Untitled Hymn" });
      nav(`/studio/${p.id}${demo ? "?demo=1" : ""}`);
    } catch (e) {
      if (e.response?.status === 402) openUpgrade(errMsg(e));
      else toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    await api.deleteProject(id).catch((e) => toast.error(errMsg(e)));
    toast("Project laid to rest");
    load();
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="library-page">
      <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080708]/70 via-[#080708]/90 to-[#080708]" />
      <header className="relative max-w-6xl mx-auto px-6 sm:px-10 h-20 flex items-center gap-6">
        <a href="/" className="font-gothic text-lg tracking-[0.15em] text-amber-100/90" data-testid="library-home-link">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</a>
        <span className="flex-1" />
        <a href="/#pricing" className="eyebrow hover:text-amber-200 transition-colors">Pricing</a>
        <AccountMenu />
      </header>
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pb-16 pt-4 grid lg:grid-cols-12 gap-14">
        <section className="lg:col-span-5 rise">
          <p className="eyebrow text-[#D99B43]">✦ Vocal harmony sanctum ✦</p>
          <h1 className="font-gothic text-4xl sm:text-5xl lg:text-6xl font-normal uppercase tracking-tight text-amber-100/90 mt-5 leading-[1.05]">
            Vox <span className="text-[#c92a3e]">Nocturna</span>
          </h1>
          <p className="font-cormorant italic text-xl text-stone-300 mt-6 leading-relaxed">
            Raise a single voice into a choir. Record or upload a vocal, summon harmonies by interval or by AI, and
            mix them in candlelit reverb.
          </p>
          <div className="mt-10 stone-panel p-5 space-y-4 tube-glow">
            <label className="eyebrow block">Name a new hymn</label>
            <input
              data-testid="new-project-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create(false)}
              placeholder="Requiem for the Moon"
              className="w-full bg-[#0f0d11] border border-[#352d40] px-3 py-2.5 text-sm outline-none focus:border-[#D99B43] transition-colors"
            />
            <div className="flex flex-wrap gap-3">
              <button data-testid="btn-create-blank-project" onClick={() => create(false)} className="btn-crimson">
                <Plus size={14} /> Create project
              </button>
              <button data-testid="btn-load-sample-project" onClick={() => create(true)} className="btn-gothic">
                <Sparkles size={14} /> Summon demo hymn
              </button>
            </div>
          </div>
        </section>
        <section className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="font-gothic text-lg uppercase tracking-[0.2em] text-stone-200">The Crypt</h2>
            <span className="flex-1 h-px bg-[#352d40]" />
            <span className="eyebrow" data-testid="project-usage">{isPro ? `${projects.length} hymns · unlimited` : `${projects.length} / 2 free hymns`}</span>
          </div>
          <div className="space-y-3" data-testid="projects-list">
            {!loading && projects.length === 0 && (
              <div className="stone-panel p-10 text-center" data-testid="projects-empty">
                <Music2 className="mx-auto text-[#5a1e28]" size={32} />
                <p className="font-cormorant italic text-lg text-stone-400 mt-3">
                  No hymns yet. Begin with a demo or create your own.
                </p>
              </div>
            )}
            {projects.map((p, i) => (
              <ProjectCard key={p.id} p={p} i={i} onOpen={() => nav(`/studio/${p.id}`)} onDelete={() => remove(p.id)} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
